export interface RumConfig {
  app_monitor_id: string
  identity_pool_id: string
  guest_role_arn?: string
  region: string
  session_sample_rate: number
  allow_cookies: boolean
  telemetries: Array<'errors' | 'performance' | 'http'>
}

export interface RuntimeConfig {
  tenant_id: string
  api: {
    /**
     * Base URL of the Cerberus identity/access API (no trailing slash).
     * The Cuidari cockpit uses this read-only surface for org, membership and
     * grant evidence. Missing => the Cerberus client keeps its env/default
     * base URL for compatibility with older runtime-config deployments.
     */
    cerberus?: string
    hephaestus?: string
    /**
     * Base URL of the Kratos operational backlog API (no trailing slash).
     * The Cuidari cockpit reads `${kratos}/kratos/backlog` read-only to show
     * active backlog and runbook pressure. Missing => the Kratos client keeps
     * its env/default base URL for compatibility with older deployments.
     */
    kratos?: string
    /**
     * Base URL of the Peitho waitlist service (no trailing slash).
     * Peitho owns waitlist + growth tooling as a standalone microservice;
     * the WaitlistForm in /login posts to `${peitho}/v1/waitlist/entries`
     * and /entrar consumes the same surface. See
     * arkheus/docs/specs/cuidari/t5b-peitho-extraction.md.
     */
    peitho?: string
    /**
     * Base URL of the Pheme messaging service (no trailing slash).
     * Pheme owns server-driven alerts/banners/badges; the `useMessages`
     * hook calls `${pheme}/v1/messages?placement=…`. See
     * arkheus/docs/specs/pheme/extraction-from-hephaestus.md.
     */
    pheme?: string
    /**
     * Base URL of the Proteus user-preferences service (no trailing slash).
     * Powers i18n/theme/notifications/LGPD via `@francisquiniai/proteus-sdk`.
     * Missing => ProteusAppProvider becomes a no-op and the app keeps
     * working off localStorage (local dev / preview).
     */
    proteus?: string
    /**
     * Base URL of the Echo product analytics INGEST relay (no trailing slash).
     * `${analytics_relay}/events` accepts batched envelopes and publishes
     * them to the `echo-events` EventBridge bus.
     *
     * NAO e mais o mesmo host de `echo`. echo#129 (deployado 2026-09-02
     * 05:22Z) moveu SO a ingestao do HttpApi `echo-relay` (2dclnn0zqk) para
     * o REST API `echo-relay-ingest` (nhe7ne4vn9) atras do WAF, e o `/v1`
     * faz parte da base. As rotas `/v1/admin/*` ficaram onde estavam — por
     * isso `echo` abaixo continua apontando para o host antigo. Enquanto
     * este valor ficou no host velho, `${analytics_relay}/events` devolvia
     * 404 e a telemetria do ariadne morria em silencio.
     *
     * Missing => the
     * AnalyticsProvider keeps the queue disabled and `useTrack` only logs
     * to `console.debug` (local dev / preview builds). Echo is an
     * ecosystem-wide multi-producer service — see francisquiniai/echo.
     */
    analytics_relay?: string
    /**
     * Base URL of the Stentor campaign orchestration service (no trailing slash).
     * The Cuidari cockpit reads campaign/template/audience registries for
     * outbound lifecycle surfaces once Stentor exposes browser-safe GETs.
     */
    stentor?: string
    /**
     * Base URL of the Iris notification delivery service admin API (no trailing slash).
     * The Cuidari cockpit reads delivery overview aggregates from
     * `${iris}/v1/admin/overview` for outbound notification health.
     */
    iris?: string
    /**
     * Base URL of the Kronos entitlement service (no trailing slash).
     * Kronos owns subscriptions, plans, feature gates and grants. The
     * subscriptions module reads `${kronos}/v1/pricing` (public) to
     * render the user plan card on /conta; future feature-gate checks
     * call `${kronos}/v1/feature-gates/{feature}` via the kronos-sdk.
     * Replaces the legacy hephaestus `/v1/subscriptions/plan` endpoint
     * (decommissioned via Fase 6 of `04-kronos-extraction.md`).
     */
    kronos?: string
    /**
     * Base URL of the Thalia AI content service (no trailing slash).
     * Thalia owns AI-generated Instagram content (multi-brand) — posts,
     * brands, assets, templates, voice profiles. The `/thalia` admin
     * route reads `${thalia}/v1/posts`, `${thalia}/v1/brands` and
     * mutates posts via `PATCH /v1/posts/{id}`. Cerberus authorizer is
     * a follow-up; today the BFF reaches the HttpApi directly.
     */
    thalia?: string
    /**
     * Base URL of the Eunomia conversation safety platform (no trailing
     * slash). Read by the `eunomia-review` admin module and by the cockpit
     * snapshots (the old `/chat` console page was removed 2026-09-29).
     * See francisquiniai/eunomia + arkheus/docs/systems/eunomia.md.
     */
    eunomia?: string
    /**
     * Base URL of the Odysseus autonomous journey quality service (no
     * trailing slash). The `/odysseus` admin route and Cuidari cockpit
     * quality snapshot read `${odysseus}/v1/journeys`, `/v1/runs` and
     * `/v1/findings`. Missing => the Odysseus client falls back to the
     * legacy production endpoint so old runtime-config deployments keep
     * the read-only dashboard functional while Janus/deploy catches up.
     */
    odysseus?: string
    /**
     * Base URL of the Aegis frontend observability service (no trailing slash).
     * The Cuidari cockpit reads `${aegis}/v1/issues`, `/v1/releases` and
     * `/v1/api-failures` for release/error health once the admin read API is
     * available. Missing => observability stays explicitly planned.
     */
    aegis?: string
    /**
     * Base URL of the Kairos shareable card service (no trailing slash).
     * The Cuidari cockpit reads `${kairos}/v1/admin/cards` to explain
     * shareable growth cards and referral/share outcomes by domain.
     */
    kairos?: string
    /**
     * Base URL of the Phronesis decisioning service (no trailing slash).
     * The Cuidari cockpit reads surfaces, moment definitions and content items
     * to show which personalization decisions are wired to each app surface.
     */
    phronesis?: string
    /**
     * Base URL of the Moirai rollout and experimentation service (no trailing slash).
     * The Cuidari cockpit reads flags and experiments to explain active gates
     * and paywall/pricing tests.
     */
    moirai?: string
    /**
     * Base URL of the Clio audit ledger service (no trailing slash).
     * The Cuidari cockpit reads audit/search with a Cerberus audit:read token
     * to show real mutation history across the participating systems.
     */
    clio?: string
    /**
     * Base URL of the Janus boundary registry service (no trailing slash).
     * The `/janus` admin route reads `${janus}/v1/admin/hosts`,
     * `${janus}/v1/admin/runtime-config`, `${janus}/v1/admin/allowlists`,
     * `${janus}/v1/admin/drift` and mutates via `PATCH /v1/admin/allowlists/{id}`
     * (60s probe + auto-rollback enforced backend-side). Missing => the
     * Janus admin dashboard renders an empty state and the health panel
     * surfaces the missing config explicitly.
     */
    janus?: string
    /**
     * Base URL of the Echo telemetry catalog admin API (no trailing slash).
     * JA FOI alias semantico de `analytics_relay`; desde echo#129 os dois
     * divergiram — a ingestao migrou para o REST API atras do WAF e SO as
     * rotas admin continuam no `echo-relay` HttpApi (2dclnn0zqk). Nao
     * reunifique os dois valores sem conferir as rotas vivas. The `/echo` admin dashboard reads
     * `${echo}/v1/admin/events`, `${echo}/v1/admin/events/aggregate`,
     * `${echo}/v1/admin/events/{id}`, `${echo}/v1/admin/producers`
     * (GET+POST) and `${echo}/v1/admin/catalog/coverage`. Missing => the
     * Echo dashboard renders an empty state and the health panel surfaces
     * the missing config explicitly. See
     * docs/cockpit/dashboards/tier-3-decision-quality.md §3.2.
     */
    echo?: string
  }
  rum?: RumConfig
}

let _config: RuntimeConfig | null = null

/**
 * Fetches /runtime-config.json on first call; caches the result.
 * The build does NOT bake this config into the bundle (binding #07).
 */
export async function getRuntimeConfig(): Promise<RuntimeConfig> {
  if (_config) return _config

  const res = await fetch('/runtime-config.json', {
    cache: 'no-store', // always fresh; CloudFront cache-control handles CDN
  })

  if (!res.ok) {
    throw new Error(
      `Failed to load runtime-config.json: ${res.status} ${res.statusText}`,
    )
  }

  _config = (await res.json()) as RuntimeConfig
  return _config
}
